import React from 'react';
import {
  ControlledChatRoomScreen,
  ChatWidget,
  expandCollapseManager,
  AVAILABILITY_STATUS,
} from '@wix/chat-web';
import { Message } from '@wix/chat-sdk';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { FAKE_AUTO_MESSAGE_DELAY } from '../../../constants/fake-auto-message-delay';
import {
  editorSettingsAvatar,
  getLocalizedTexts,
  theme,
} from '../../../reducers';
import { connect } from 'react-redux';
import wix from '../../../services/wix';
import _get from 'lodash/get';
import * as biLogger from '../../../services/biLogger';
import { collapseWidget, expandWidget } from '../../../actions';
import { fedopsLogger } from '../../../services/fedopsLogger';
import * as Sentry from '@sentry/browser';

interface DummyWidgetProps extends InjectedTranslateProps {
  theme: any;
  locale: string;
  editorSettingsBehaviour: any;
  localizedTexts: any;
  editorSettingsAvatar: any;
  editorSettingsLayout: any;
  autoMessage: string;
  widgetExpanded: boolean;
  expandWidget(): any;
  collapseWidget(): any;
}

interface DummyWidgetState {
  messages: Message[];
}

class DummyWidget extends React.Component<DummyWidgetProps, DummyWidgetState> {
  constructor(props) {
    super(props);
    this.state = {
      messages: [],
    };
  }

  componentDidMount() {
    Sentry.addBreadcrumb({ category: 'MountedWidget', message: 'Dummy' });
    wix.resizeToCollapsedMode(this.isCollapsedAsRounded()).catch();
    expandCollapseManager.registerOnExpand(this.handleExpand);
    expandCollapseManager.registerOnCollapse(this.handleCollapse);
  }

  componentWillUnmount() {
    expandCollapseManager.unregisterOnExpand(this.handleExpand);
    expandCollapseManager.unregisterOnCollapse(this.handleCollapse);
  }

  handleExpand = () => {
    this.props.expandWidget();
  };

  handleCollapse = () => {
    this.props.collapseWidget();
  };

  isCollapsedAsRounded = () => {
    return _get(this.props.editorSettingsLayout, 'collapsedAsRounded', false);
  };

  handleSendMessage = (message) => {
    this.addMessage(message);
    this.sendAutoMessageWithDelay();
  };

  addMessage = (message: Message) => {
    this.setState((prevState) => ({
      messages: [...prevState.messages, message],
    }));
  };

  sendAutoMessageWithDelay = () => {
    const { autoMessage } = this.props;
    setTimeout(() => {
      this.addMessage(
        Message.fromText({
          text: autoMessage,
          userId: 'fake-response-user-id',
        } as any),
      );
    }, FAKE_AUTO_MESSAGE_DELAY);
  };

  onChatDisplayed = () => {
    const asRounded = _get(
      this.props.editorSettingsLayout,
      'collapsedAsRounded',
      false,
    );
    const layoutName = asRounded
      ? 'collapsedAsRounded'
      : 'selectedRadiusOption';
    biLogger.chatDisplayedEvent(wix.getDeviceType(), layoutName);
    fedopsLogger.appLoaded();
  };

  eventHooks = {
    onChatDisplayed: this.onChatDisplayed,
  };

  render() {
    const { messages } = this.state;
    const {
      editorSettingsAvatar: avatar,
      editorSettingsBehaviour: { responseTimeEnabled, responseTimeSelected },
      localizedTexts: { onlineTitle, onlineMessage },
      widgetExpanded,
    } = this.props;
    const typicallyReplies = {
      isEnabled: responseTimeEnabled,
      period: responseTimeSelected,
    };
    return (
      <ChatWidget
        isExpanded={widgetExpanded}
        onlineTitle={onlineTitle}
        onlineMessage={onlineMessage}
        avatar={avatar}
        theme={this.props.theme}
        locale={this.props.locale}
        eventHooks={this.eventHooks}
        availabilityStatus={AVAILABILITY_STATUS.AVAILABLE}
        typicallyReplies={typicallyReplies}
      >
        <ControlledChatRoomScreen
          messages={messages as any[]}
          onSendMessage={this.handleSendMessage}
          locale={this.props.locale}
          theme={this.props.theme}
        />
      </ChatWidget>
    );
  }
}

function mapStateToProps(state) {
  return {
    theme: theme(state),
    editorSettingsAvatar: editorSettingsAvatar(state),
    localizedTexts: getLocalizedTexts(state),
    editorSettingsBehaviour: state.editorSettingsBehaviour,
    editorSettingsLayout: state.editorSettingsLayout,
    locale: state.locale,
    widgetExpanded: state.widgetExpanded,
  };
}

const mapDispatchToProps = (dispatch) => ({
  expandWidget: () => dispatch(expandWidget()),
  collapseWidget: () => dispatch(collapseWidget()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(translate()(DummyWidget));
